import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Users, Star, Check, Phone, Mail, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { mockBookings, tours, pickupLocations, testimonials, agendaItems } from '../mockData';
import { useToast } from '../hooks/use-toast';
import WeatherClock from '../components/WeatherClock';
import StatsCounter from '../components/StatsCounter';
import VideoShowcase from '../components/VideoShowcase';

const BrandyCeremonyBanner = () => {
  const navigate = useNavigate();
  return (
    <section className="relative py-14 md:py-16 px-4 overflow-hidden" data-testid="brandy-ceremony-banner">
      {/* Dark luxe background */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-[#0d0b07] to-gray-950" />
      <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/4700108/pexels-photo-4700108.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-[0.06]" />
      {/* Gold accent line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />

      <div className="container mx-auto max-w-5xl relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
          {/* Left — star visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex-shrink-0"
          >
            <div className="relative">
              <div className="h-28 w-28 md:h-36 md:w-36 rounded-full border-2 border-yellow-500/30 flex items-center justify-center bg-yellow-500/[0.05] backdrop-blur-sm shadow-[0_0_40px_rgba(212,175,55,0.1)]">
                <div className="text-center">
                  <Star className="h-6 w-6 md:h-8 md:w-8 text-yellow-500 mx-auto mb-1 fill-yellow-500/30" />
                  <p className="text-[10px] text-white/30 tracking-widest uppercase">The 2,839th Star</p>
                </div>
              </div>
              {/* Pulse ring */}
              <div className="absolute inset-0 rounded-full border border-yellow-500/10 animate-ping" style={{ animationDuration: '3s' }} />
            </div>
          </motion.div>

          {/* Center — copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex-1 text-center md:text-left"
          >
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-3">
              <Badge className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-[10px] tracking-widest uppercase px-3 py-1">
                Upcoming VIP Event
              </Badge>
              <Badge className="bg-white/[0.05] text-white/40 border border-white/10 text-[10px] tracking-widest uppercase px-3 py-1">
                March 30, 2026
              </Badge>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              <span className="gold-gradient-text">Brandy</span> Walk of Fame Star Ceremony
            </h2>
            <p className="text-sm text-white/40 leading-relaxed max-w-lg mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Witness history with the original IATA-accredited Hollywood tour operator. Preferred viewing. Escorted arrival. Limited-edition keepsake.
            </p>
            <p className="text-xs text-white/25">
              W Hollywood Hotel — 10:15 AM Check-in — Space is strictly limited
            </p>
          </motion.div>

          {/* Right — CTA */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex-shrink-0"
          >
            <Button
              size="lg"
              data-testid="brandy-banner-cta"
              className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-500 text-black px-8 py-6 text-sm font-black tracking-wide shadow-[0_0_30px_rgba(212,175,55,0.25)] hover:shadow-[0_0_50px_rgba(212,175,55,0.45)] transition-all duration-500 border-0 rounded-full"
              onClick={() => navigate('/brandy-ceremony-2026')}
            >
              VIEW VIP PACKAGE
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  const [currentBooking, setCurrentBooking] = useState(0);
  const [timeLeft, setTimeLeft] = useState({ hours: 3, minutes: 0, seconds: 0 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    partySize: 4,
    tourType: 'main-tour',
    customDetails: '',
    addons: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rotate booking notifications
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBooking((prev) => (prev + 1) % mockBookings.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for Early Bird special
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return { hours: 3, minutes: 0, seconds: 0 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/booking`, formData);
      
      toast({
        title: "Booking Submitted!",
        description: "Thank you! We will contact you shortly to confirm your tour.",
        variant: "default",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        partySize: 4,
        tourType: 'main-tour',
        customDetails: '',
        addons: []
      });
      
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Saved",
        description: "Your booking has been saved. We will contact you shortly!",
        variant: "default",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleAddon = (addonId) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addonId)
        ? prev.addons.filter(id => id !== addonId)
        : [...prev.addons, addonId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Weather & Clock Widget */}
      <WeatherClock />
      
      {/* Booking Notification Widget with Animation */}
      <motion.div 
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 1, type: "spring" }}
        className="fixed bottom-6 left-6 z-50"
      >
        <Card className="bg-black/90 backdrop-blur-xl shadow-[0_0_30px_rgba(212,175,55,0.15)] border border-yellow-600/30 w-80 hover:scale-105 transition-transform" data-testid="booking-notification">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <motion.div 
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-600 to-yellow-500 flex items-center justify-center flex-shrink-0"
              >
                <Users className="h-5 w-5 text-black" />
              </motion.div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-white truncate text-sm">
                  {mockBookings[currentBooking].name} from {mockBookings[currentBooking].location}
                </p>
                <p className="text-xs font-semibold flex items-center gap-1" style={{ color: '#e8d48b' }}>
                  <Sparkles className="h-3 w-3" />
                  Just booked - Party of {mockBookings[currentBooking].partySize}!
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {mockBookings[currentBooking].timeAgo}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* HERO SECTION — BILLION DOLLAR WEALTH */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover scale-105"
            poster="https://images.unsplash.com/photo-1594663805807-29a7cc1847c0?auto=format&fit=crop&w=1920&q=80"
          >
            <source src="https://customer-assets.emergentagent.com/job_celeb-tour-official/artifacts/7gf6w4lj_IMG_2426.MP4" type="video/mp4" />
          </video>
          {/* Cinematic overlay — dark bottom for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
          {/* Gold shimmer sweep */}
          <div className="absolute inset-0 hero-gold-shimmer opacity-20" />
        </div>

        {/* Floating Gold Particles */}
        <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
          {[...Array(18)].map((_, i) => (
            <div
              key={i}
              className="gold-particle"
              style={{
                left: `${5 + (i * 5.3) % 90}%`,
                animationDuration: `${6 + (i % 5) * 2}s`,
                animationDelay: `${(i * 0.7) % 5}s`,
              }}
            />
          ))}
        </div>

        {/* USA Flag — subtle luxury placement */}
        <div className="absolute top-6 right-6 z-20" data-testid="usa-flag">
          <div className="bg-black/20 backdrop-blur-xl rounded-full p-3 border border-white/10 shadow-[0_0_30px_rgba(212,175,55,0.15)]">
            <span className="text-4xl">🇺🇸</span>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          {/* Exclusive Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mb-10"
          >
            <span className="inline-flex items-center gap-2.5 px-6 py-2.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] backdrop-blur-xl text-xs sm:text-sm tracking-[0.25em] uppercase" style={{ color: '#e8d48b' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              #1 Hollywood Sightseeing Tours in Los Angeles
            </span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5 }}
            className="mb-6"
            data-testid="hero-title"
          >
            <span
              className="block leading-[0.85] tracking-tight text-white"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(3.5rem, 9vw, 9rem)',
                fontWeight: 900,
                textShadow: '0 4px 40px rgba(0,0,0,0.4)',
              }}
            >
              HOLLYWOOD
            </span>
            <span
              className="block leading-[0.85] tracking-tight gold-gradient-text"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(3.5rem, 9vw, 9rem)',
                fontWeight: 900,
              }}
            >
              FAME TOUR
            </span>
          </motion.h1>

          {/* Luxury Ornamental Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="flex items-center justify-center gap-4 mb-8"
          >
            <div className="h-px w-20 md:w-32 bg-gradient-to-r from-transparent to-yellow-500/60" />
            <div className="w-2.5 h-2.5 rotate-45 border border-yellow-500/60" />
            <div className="h-px w-20 md:w-32 bg-gradient-to-l from-transparent to-yellow-500/60" />
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="text-base md:text-lg text-white/75 max-w-2xl mx-auto mb-14 font-light tracking-wide leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            A curated, all-inclusive Hollywood sightseeing tour with gourmet breakfast &amp; lunch.
            <br />
            <span style={{ color: '#e8d48b' }}>Private VIP Hollywood tours &amp; exclusive group experiences — starting at $300/person.</span>
          </motion.p>

          {/* Luxury Stat Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="flex flex-wrap gap-4 md:gap-6 justify-center mb-14"
            data-testid="hero-stats"
          >
            {[
              { value: '$300', label: 'Per Guest', sub: 'All-Inclusive' },
              { value: '5.0', label: 'Rating', sub: '5,000+ Reviews' },
              { value: '100+', label: 'Attractions', sub: 'Exclusive Access' },
              { value: 'VIP', label: 'Service', sub: 'White Glove' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="relative group"
              >
                <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-yellow-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-white/[0.06] backdrop-blur-2xl rounded-xl px-6 py-4 md:px-8 md:py-5 border border-white/[0.1] min-w-[120px]">
                  <p className="text-xl md:text-2xl font-black gold-gradient-text">{stat.value}</p>
                  <p className="text-[11px] text-white/60 font-medium tracking-widest uppercase mt-1">{stat.label}</p>
                  <p className="text-[9px] text-white/30 mt-0.5">{stat.sub}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            <Button
              size="lg"
              data-testid="hero-book-now-btn"
              className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 hover:from-yellow-500 hover:via-yellow-400 hover:to-yellow-500 text-black px-10 py-7 text-base md:text-lg font-black tracking-wide shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:shadow-[0_0_60px_rgba(212,175,55,0.55)] transition-all duration-500 border-0 rounded-full"
              onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
            >
              RESERVE YOUR EXPERIENCE
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              data-testid="hero-view-tours-btn"
              className="bg-transparent border border-white/25 text-white hover:bg-white/10 hover:border-white/40 px-10 py-7 text-base md:text-lg font-medium tracking-wide transition-all duration-500 rounded-full backdrop-blur-md"
              onClick={() => document.getElementById('tours').scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Tours
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.2 }}
            className="mt-16 flex flex-wrap items-center justify-center gap-6 md:gap-8 text-white/30 text-[10px] md:text-xs tracking-[0.2em] uppercase"
            data-testid="hero-trust-badges"
          >
            <span>As Featured In</span>
            <span className="text-white/50 font-semibold">Travel + Leisure</span>
            <span className="w-1 h-1 rounded-full bg-yellow-500/40" />
            <span className="text-white/50 font-semibold">Forbes Travel</span>
            <span className="w-1 h-1 rounded-full bg-yellow-500/40" />
            <span className="text-white/50 font-semibold">Conde Nast</span>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="flex flex-col items-center gap-2 text-white/30">
            <span className="text-[9px] tracking-[0.35em] uppercase">Scroll</span>
            <div className="w-px h-8 bg-gradient-to-b from-white/30 to-transparent" />
          </div>
        </motion.div>
      </section>

      {/* VIP Brandy Star Ceremony Banner */}
      <BrandyCeremonyBanner />

      <StatsCounter />
      <VideoShowcase />

      {/* Early Bird Special Banner */}
      <section className="bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950 py-5 border-y border-yellow-600/20" data-testid="early-bird-banner">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-500/10 p-2.5 rounded-full border border-yellow-500/20">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white tracking-wide">EARLY BIRD SPECIAL</h3>
                <p className="text-xs text-white/50 tracking-wider uppercase">Book now and save 10%</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-center">
              <div className="bg-white/[0.05] backdrop-blur-md rounded-lg px-4 py-2 min-w-[65px] border border-white/10">
                <p className="text-2xl font-black gold-gradient-text">{String(timeLeft.hours).padStart(2, '0')}</p>
                <p className="text-[9px] text-white/40 tracking-widest uppercase">Hours</p>
              </div>
              <span className="text-xl font-black text-yellow-500/50">:</span>
              <div className="bg-white/[0.05] backdrop-blur-md rounded-lg px-4 py-2 min-w-[65px] border border-white/10">
                <p className="text-2xl font-black gold-gradient-text">{String(timeLeft.minutes).padStart(2, '0')}</p>
                <p className="text-[9px] text-white/40 tracking-widest uppercase">Min</p>
              </div>
              <span className="text-xl font-black text-yellow-500/50">:</span>
              <div className="bg-white/[0.05] backdrop-blur-md rounded-lg px-4 py-2 min-w-[65px] border border-white/10">
                <p className="text-2xl font-black gold-gradient-text">{String(timeLeft.seconds).padStart(2, '0')}</p>
                <p className="text-[9px] text-white/40 tracking-widest uppercase">Sec</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tours Section - Enhanced */}
      <section id="tours" className="py-20 px-4 relative overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-yellow-50 opacity-70" />
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-red-200 to-pink-200 rounded-full filter blur-3xl opacity-30"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-600 font-semibold mb-4">Curated Hollywood Sightseeing Experiences</p>
            <h2 className="text-4xl sm:text-5xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Hollywood Tours &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600">Attractions</span>
            </h2>
            <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">
              From the Hollywood Walk of Fame to Beverly Hills mansions — every Hollywood sightseeing tour is handcrafted for the discerning traveler
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {tours.map((tour, idx) => (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
              >
                <Card 
                  className={`overflow-hidden hover:shadow-2xl transition-all duration-500 group ${
                    tour.featured ? 'border-2 border-yellow-500/50 shadow-[0_0_20px_rgba(212,175,55,0.1)]' : 'border border-gray-200'
                  }`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <motion.img 
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={tour.image} 
                      alt={tour.title} 
                      className="w-full h-full object-cover"
                    />
                    {tour.badge && (
                      <motion.div
                        initial={{ x: 100 }}
                        animate={{ x: 0 }}
                        className="absolute top-4 right-4"
                      >
                        <Badge className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black px-4 py-2 text-lg font-bold shadow-lg">
                          {tour.badge}
                        </Badge>
                      </motion.div>
                    )}
                    {tour.featured && (
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute top-4 left-4"
                      >
                        <Badge className="bg-yellow-500 text-black px-4 py-2 text-lg font-bold shadow-lg flex items-center gap-1">
                          <Sparkles className="h-4 w-4" />
                          FEATURED
                        </Badge>
                      </motion.div>
                    )}
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <p className="text-white text-sm">{tour.subtitle}</p>
                    </div>
                  </div>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <CardTitle className="text-2xl font-black text-gray-900">{tour.title}</CardTitle>
                        <p className="text-gray-600 mt-1">{tour.subtitle}</p>
                      </div>
                      <motion.div 
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="text-right"
                      >
                        <p className="text-4xl font-black bg-gradient-to-r from-yellow-600 to-yellow-500 bg-clip-text text-transparent">
                          ${tour.price}
                        </p>
                        <p className="text-sm text-gray-500">{tour.duration}</p>
                      </motion.div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {tour.includes.slice(0, 5).map((item, idx) => (
                        <motion.li 
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-start gap-2"
                        >
                          <Check className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </motion.li>
                      ))}
                      {tour.includes.length > 5 && (
                        <li className="text-sm text-gray-500 italic">+ {tour.includes.length - 5} more...</li>
                      )}
                    </ul>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-black font-bold py-6 text-lg shadow-lg group relative overflow-hidden"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            tourType: tour.id,
                            addons: tour.isAddon ? [tour.id] : prev.addons
                          }));
                          document.getElementById('booking').scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          {tour.isAddon ? 'Add to Tour' : 'Book This Tour'}
                          <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform text-black" />
                        </span>
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: 0 }}
                          transition={{ duration: 0.3 }}
                        />
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tour Agenda */}
      <TourAgendaSection />

      {/* Pickup Locations */}
      <PickupLocationsSection />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* Booking Form */}
      <BookingFormSection 
        formData={formData} 
        handleInputChange={handleInputChange} 
        handleSubmit={handleSubmit} 
        toggleAddon={toggleAddon}
        tours={tours}
        isSubmitting={isSubmitting}
      />

      {/* Footer */}
      <FooterSection />
    </div>
  );
};

// Import sections from HomePageContinued
const TourAgendaSection = () => {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-yellow-600 font-semibold mb-4">Full-Day All-Inclusive Hollywood Sightseeing Itinerary</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Hollywood Sightseeing Tour</span> Day
          </h2>
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">Every Hollywood attraction on your all-inclusive sightseeing tour — from sunrise breakfast to sunset memories at the Walk of Fame</p>
        </div>

        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-yellow-500 to-yellow-600/30 hidden md:block" />

          <div className="space-y-12">
            {agendaItems.map((item, index) => (
              <div 
                key={item.step}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <Card className="hover:shadow-xl transition-shadow">
                    <CardContent className="p-6">
                      <Badge className="mb-2 bg-yellow-600 text-black">{item.time}</Badge>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="relative z-10 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center shadow-lg">
                    <span className="text-white text-2xl font-black">{item.step}</span>
                  </div>
                </div>

                <div className="flex-1 hidden md:block" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PickupLocationsSection = () => (
  <section className="py-20 px-4 bg-white">
    <div className="container mx-auto max-w-7xl">
      <div className="text-center mb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-yellow-600 font-semibold mb-4">Complimentary Hotel Pickup for All Hollywood Tours</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Hotel Pickup</span> Locations
        </h2>
        <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">
          Free pickup from major hotels across Hollywood, Beverly Hills, West Hollywood, Santa Monica &amp; Downtown LA — included with every Hollywood sightseeing tour
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {pickupLocations.map((location) => (
          <Card key={location.area} className="hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-br from-yellow-600 to-yellow-500 text-black">
              <div className="flex items-center gap-2">
                <MapPin className="h-6 w-6" />
                <CardTitle className="text-xl font-black">{location.area}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <ul className="space-y-2">
                {location.hotels.map((hotel, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-600 font-bold">•</span>
                    <span className="text-gray-700">{hotel}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Card className="bg-gradient-to-r from-yellow-600 to-yellow-500 border-none text-black inline-block">
          <CardContent className="p-6">
            <p className="text-lg font-bold mb-2">Don't see your hotel?</p>
            <p className="text-sm">No problem! We can arrange pickup from any location in the area.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="py-20 px-4 bg-gray-50">
    <div className="container mx-auto max-w-7xl">
      <div className="text-center mb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-yellow-600 font-semibold mb-4">5,000+ Five-Star Hollywood Tour Reviews</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Guests Say</span>
        </h2>
        <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">Join thousands of happy travelers who've experienced the best Hollywood sightseeing tours and attractions in Los Angeles</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, idx) => (
          <Card key={idx} className="hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
              <div className="border-t pt-4">
                <p className="font-bold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-600">{testimonial.location}</p>
                <Badge className="mt-2 bg-yellow-600 text-black">Party of {testimonial.partySize}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const BookingFormSection = ({ formData, handleInputChange, handleSubmit, toggleAddon, tours, isSubmitting }) => (
  <section id="booking" className="py-20 px-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
    <div className="container mx-auto max-w-4xl">
      <div className="text-center mb-12">
        <p className="text-xs tracking-[0.3em] uppercase text-yellow-500/70 font-semibold mb-4">Book Your Hollywood Sightseeing Tour</p>
        <h2 className="text-4xl sm:text-5xl font-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
          Be the <span className="gold-gradient-text">Celebrity</span> for the Day
        </h2>
        <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto">
          Tell us your dream Hollywood experience — from Walk of Fame tours to exclusive Hollywood attractions. We'll craft a VIP itinerary just for you. Limited availability — reserve now.
        </p>
      </div>

      <Card className="border border-yellow-500/30 bg-white text-gray-900 shadow-[0_0_40px_rgba(212,175,55,0.1)]">
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">

            {/* Dream Tour Description — hero field */}
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-300/50 rounded-xl p-6">
              <label className="block text-sm font-black text-gray-800 mb-1">
                Describe Your Dream Hollywood Experience
              </label>
              <p className="text-xs text-gray-500 mb-3">Celebrity homes? Rodeo Drive shopping? Nightlife? A private limo with champagne? Tell us everything.</p>
              <Textarea
                name="customDetails"
                value={formData.customDetails}
                onChange={handleInputChange}
                placeholder="e.g., We're a group of 6 friends celebrating a birthday! We'd love a limo ride through Beverly Hills, shop on Rodeo Drive, see celebrity homes, and end the night at the best Hollywood clubs..."
                rows={4}
                className="w-full border-yellow-300 focus:border-yellow-500"
                data-testid="dream-tour-textarea"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                  className="w-full"
                  data-testid="booking-name"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@example.com"
                  className="w-full"
                  data-testid="booking-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Phone *</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="+1 (555) 123-4567"
                  className="w-full"
                  data-testid="booking-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Party Size (Min 4) *</label>
                <Input
                  type="number"
                  name="partySize"
                  min="4"
                  value={formData.partySize}
                  onChange={handleInputChange}
                  required
                  className="w-full"
                  data-testid="booking-party-size"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Preferred Date</label>
                <Input
                  type="date"
                  name="preferredDate"
                  onChange={handleInputChange}
                  className="w-full"
                  data-testid="booking-date"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Select Tour *</label>
              <select
                name="tourType"
                value={formData.tourType}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-md"
                required
                data-testid="booking-tour-select"
              >
                {tours.filter(t => !t.isAddon).map(tour => (
                  <option key={tour.id} value={tour.id}>{tour.title} - ${tour.price}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">Add-Ons (Optional)</label>
              <div className="grid md:grid-cols-3 gap-4">
                {tours.filter(t => t.isAddon).map(addon => (
                  <Card 
                    key={addon.id}
                    className={`cursor-pointer transition-all ${
                      formData.addons.includes(addon.id) 
                        ? 'border-2 border-yellow-500 bg-yellow-50' 
                        : 'border border-gray-300 hover:border-yellow-400'
                    }`}
                    onClick={() => toggleAddon(addon.id)}
                    data-testid={`addon-${addon.id}`}
                  >
                    <CardContent className="p-4 text-center">
                      <p className="font-bold text-sm mb-1">{addon.title}</p>
                      <p className="text-2xl font-black text-yellow-600">+${addon.price}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <Button 
              type="submit"
              disabled={isSubmitting}
              data-testid="booking-submit-btn"
              className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-black font-black py-7 text-xl shadow-[0_0_30px_rgba(212,175,55,0.3)] hover:shadow-[0_0_50px_rgba(212,175,55,0.5)] hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>

            <p className="text-center text-sm text-gray-600">
              * Secure payment details will be collected after we confirm availability
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  </section>
);

const FooterSection = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-gray-900 text-white py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#e8d48b' }}>Hollywood Fame Tour</h3>
            <p className="text-gray-400 mb-4">
              The #1 rated all-inclusive Hollywood sightseeing tours in Los Angeles. Visit 100+ Hollywood attractions including the Walk of Fame, Beverly Hills, Rodeo Drive, Hollywood Sign, and celebrity homes. Gourmet breakfast &amp; lunch, VIP transportation included.
            </p>
            <p className="text-sm text-gray-500">
              Powered by{' '}
              <a 
                href="https://officialhollywoodtours.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-500 hover:underline"
              >
                Official Hollywood Tours LLC
              </a>
            </p>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-lg">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#tours" className="hover:text-yellow-500 transition-colors">Our Tours</a></li>
              <li><a href="#booking" className="hover:text-yellow-500 transition-colors">Book Now</a></li>
              <li><button onClick={() => navigate('/brandy-ceremony-2026')} className="hover:text-yellow-500 transition-colors text-left">Brandy VIP Ceremony</button></li>
              <li><button onClick={() => navigate('/concierge')} className="hover:text-yellow-500 transition-colors text-left">Hotel Concierge</button></li>
              <li><button onClick={() => navigate('/events')} className="hover:text-yellow-500 transition-colors text-left">Submit Event</button></li>
              <li><a href="https://officialhollywoodtours.com" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-500 transition-colors">Vacation Packages</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-lg">Hollywood Tours</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Hollywood Sightseeing Tour</li>
              <li>Hollywood Walk of Fame Tour</li>
              <li>Music Studio Experience</li>
              <li>Melrose &amp; Rodeo Drive Shopping</li>
              <li>Hollywood Nightclub Hop</li>
              <li>Hollywood Attractions Tour</li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-lg">Contact Us</h4>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-yellow-500" />
                <span>24/7: (877) 741-8390</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-yellow-500" />
                <span>info@officialhollywoodtours.com</span>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-yellow-500" />
                <span>Hollywood, California</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-yellow-500" />
                <span>Fax: (623) 236-2113</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
          <p>&copy; 2026 Hollywood Fame Tour. All rights reserved.</p>
          <p className="mt-2">
            Powered by{' '}
            <a 
              href="https://officialhollywoodtours.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-yellow-400 hover:underline"
            >
              Official Hollywood Tours LLC
            </a>
          </p>
          <div className="mt-4">
            <a
              href="https://kholey.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-gray-600 hover:text-yellow-500 transition-colors"
              data-testid="diamond-footer-link"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="opacity-50 hover:opacity-100 transition-opacity">
                <path d="M6 2L1 9h22L18 2H6zm0 1.5h12l3.5 5H2.5L6 3.5z"/>
                <path d="M1 9l11 13L23 9H1z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default HomePage;
